import { appConfigs } from '@configs/apps/appConfigs';
import { useBaselineCalculation } from '../../../hooks/useBaselineCalculation';
import { useHeartRateStore } from '@store/useHeartRateStore';
import { getHeartRateState } from '@utils/getHeartState';
import { useSessionEventManager } from '@utils/sessionEventManager';
import { Activity } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAbly } from '../../../providers/AblyProvider';
import { useSession } from '../../../hooks/useSession';
import PhaseDetailsAccordion from './DebugPhase';
import { StatGeneric } from './Stat/Stat';

function StatPhaseComponent() {
  const { id } = useParams();
  const { sendMessage } = useAbly();
  const { session: currentExperience } = useSession(id);

  // Inizializza il SessionEventManager
  const eventManager = useSessionEventManager(
    currentExperience?.type || 'default',
    currentExperience?.id?.toString() || '',
    currentExperience ?? null,
    sendMessage
  );

  const phase = useHeartRateStore(s => s.phase);
  const dataState = useHeartRateStore(s => s.events);
  const { baseline: storeBaseline } = useBaselineCalculation();
  const setPhase = useHeartRateStore(s => s.setPhase);
  const setPhaseEvent = useHeartRateStore(s => s.setPhaseEvent);

  const variation = currentExperience?.config?.variation;
  // Usa baseline dallo store; se 0, fallback su session.baseline (es. dopo refresh o sync non ancora avvenuta)
  const baseline =
    storeBaseline > 0
      ? storeBaseline
      : typeof currentExperience?.baseline === 'number' &&
          currentExperience.baseline > 0
        ? currentExperience.baseline
        : 0;

  const isBioFeedbackSession = useCallback(() => {
    return (
      appConfigs[currentExperience?.type as keyof typeof appConfigs]
        ?.defaultVariation > 0
    );
  }, [currentExperience?.type]);

  const checkHeartRateState = useCallback(() => {
    if (baseline === 0) return;
    if (dataState.length === 0) return;
    if (!isBioFeedbackSession()) return;

    const effectiveVariation = variation === 0 ? 5 : variation || 5;
    const newState = getHeartRateState(dataState, baseline, effectiveVariation);
    const latestHr = dataState.at(-1)?.hr || -1;

    if (newState !== phase) {
      setPhase(newState as any);
      if (latestHr !== -1) {
        setPhaseEvent(newState as any, latestHr);
      }
    }

    eventManager.change(newState);
  }, [
    dataState,
    baseline,
    variation,
    phase,
    setPhase,
    setPhaseEvent,
    eventManager,
    isBioFeedbackSession,
  ]);

  useEffect(() => {
    checkHeartRateState();
  }, [checkHeartRateState]);

  if (!isBioFeedbackSession()) return null;
  return <PhaseDetailsAccordion />;
}

export default function StatPhase() {
  const { id } = useParams();
  const { session: currentExperience } = useSession(id);
  const phaseEvents = useHeartRateStore(s => s.phaseEvents);

  const lastPhaseValue = useMemo(() => {
    const lastEvent = phaseEvents.at(-1);
    return lastEvent?.phase.toUpperCase() || '--';
  }, [phaseEvents]);

  return currentExperience?.status === 'completed' ? (
    <StatGeneric icon={Activity} label='Ultimo stato' value={lastPhaseValue} />
  ) : (
    <StatPhaseComponent />
  );
}
